// PricingDetails/MaterialSelector/MaterialSelector.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fetchRawMaterialsList } from '../../../../../../utils/api-service/get-services';
import { getMethod } from '../../../../../../utils/api-service/api-service';

const MaterialSelector = ({
    categorySlug,
    value,
    onChange,
    filters = {},
    placeholder = "Search material...",
    displayFields = ["name", "code", "selling_price_per_unit", "unit"],
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [categoryConfig, setCategoryConfig] = useState(null);
    const wrapperRef = useRef(null);
    const debounceTimerRef = useRef(null);

    // Fetch category configuration to know the column headers
    useEffect(() => {
        const fetchCategoryConfig = async () => {
            if (!categorySlug) return;
            try {
                const response = await getMethod(`/raw-material-category/slug/${categorySlug}/table-columns`);
                const data = response;
                if (data.success) {
                    setCategoryConfig(data.data);
                }
            } catch (error) {
                console.error('Error fetching category config:', error);
            }
        };
        fetchCategoryConfig();
    }, [categorySlug]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setMaterials([]);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Search materials when searchTerm changes
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (!searchTerm || searchTerm.length < 2) {
            setMaterials([]);
            return;
        }

        setLoading(true);

        debounceTimerRef.current = setTimeout(async () => {
            try {
                const response = await fetchRawMaterialsList(searchTerm, categorySlug);
                if (response.success) {
                    let filteredMaterials = response.materials || [];

                    // Apply filters if any
                    if (Object.keys(filters).length > 0) {
                        filteredMaterials = filteredMaterials.filter(material => {
                            return Object.entries(filters).every(([key, values]) => {
                                if (key.includes('.')) {
                                    const [parent, child] = key.split('.');
                                    if (parent === 'properties') {
                                        const propValue = material.properties?.[child];
                                        return values.includes(propValue);
                                    }
                                    if (parent === 'vendorProperties') {
                                        const vendorPropValue = material.vendorProperties?.[child];
                                        return values.includes(vendorPropValue);
                                    }
                                }
                                const materialValue = material[key];
                                return values.includes(materialValue);
                            });
                        });
                    }

                    setMaterials(filteredMaterials);
                }
            } catch (error) {
                console.error('Error fetching materials:', error);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchTerm, categorySlug, filters]);

    const handleSelect = (material) => {
        onChange(material);
        setIsOpen(false);
        setSearchTerm('');
        setMaterials([]);
    };

    const handleClear = () => {
        onChange(null);
        setSearchTerm('');
        setMaterials([]);
    };

    const formatPrice = (material) => {
        return `₹${material.selling_price_per_unit}/${material.unit}`;
    };

    // Get vendor properties with their mapped column headers
    const getMappedVendorProperties = (material) => {
        if (!material.vendorProperties || !material.vendor) return [];

        const vendor = material.vendor;
        const propertyDefs = vendor.vendorItemPropertyDefinitions || [];

        const mappedProps = [];

        propertyDefs.forEach(def => {
            const value = material.vendorProperties[def.name];
            if (value && def.mappedToColumn) {
                mappedProps.push({
                    columnHeader: def.mappedToColumn,
                    label: def.label,
                    value: value,
                    name: def.name
                });
            }
        });

        // Sort by display order from category config if available
        if (categoryConfig?.tableColumns) {
            mappedProps.sort((a, b) => {
                const orderA = categoryConfig.tableColumns.find(col => col.header === a.columnHeader)?.displayOrder || 999;
                const orderB = categoryConfig.tableColumns.find(col => col.header === b.columnHeader)?.displayOrder || 999;
                return orderA - orderB;
            });
        }

        return mappedProps;
    };

    // Get the primary display value (first visible mapped column or first in display order)
    const getPrimaryDisplayValue = (material) => {
        const mappedProps = getMappedVendorProperties(material);

        if (mappedProps.length > 0) {
            // Find the first visible column from category config
            const visibleColumns = categoryConfig?.tableColumns?.filter(col => col.isVisible) || [];

            if (visibleColumns.length > 0) {
                // Try to find a mapped property that matches the first visible column
                const primaryColumn = visibleColumns[0];
                const matchingProp = mappedProps.find(p => p.columnHeader === primaryColumn.header);
                if (matchingProp) {
                    return matchingProp.value;
                }
            }
            // Fallback to first mapped property
            return mappedProps[0].value;
        }

        return material.name || '';
    };

    // Get display text for selected value (shows all mapped properties)
    const getDisplayText = (value) => {
        if (!value) return '';
        if (value._material) value = value._material;
        const mappedProps = getMappedVendorProperties(value);

        if (mappedProps.length > 0) {
            // Show all mapped properties separated by " | "
            const displayParts = mappedProps.map(prop => `${prop.value}`);
            if (value?.vendor && value?.vendor?.name) displayParts.push(value?.vendor?.name);
            return displayParts.join(' | ');
        }

        // Fallback to displayFields
        const fields = displayFields.map(field => {
            if (field === 'selling_price_per_unit') {
                return formatPrice(value);
            }
            if (field.includes('.')) {
                const parts = field.split('.');
                if (parts[0] === 'properties') {
                    return value.properties?.[parts[1]] || '';
                }
                if (parts[0] === 'vendorProperties') {
                    return value.vendorProperties?.[parts[1]] || '';
                }
                if (parts[0] === 'specifications') {
                    return value.specifications?.[parts[1]] || '';
                }
            }
            return value[field] || '';
        }).filter(Boolean);

        return fields.join(' | ');
    };

    // Get display info for search results (shows all mapped properties)
    const getMaterialDisplayInfo = (material) => {
        const displayInfo = [];

        // Get mapped vendor properties
        const mappedProps = getMappedVendorProperties(material);
        const visibleColumns = categoryConfig?.tableColumns?.filter(col => col.isVisible) || [];

        // Show all mapped vendor properties in the order defined by category config
        if (mappedProps.length > 0) {
            // Sort by display order from category config
            const sortedMappedProps = [...mappedProps].sort((a, b) => {
                const orderA = categoryConfig?.tableColumns?.find(col => col.header === a.columnHeader)?.displayOrder || 999;
                const orderB = categoryConfig?.tableColumns?.find(col => col.header === b.columnHeader)?.displayOrder || 999;
                return orderA - orderB;
            });

            sortedMappedProps.forEach(prop => {
                // Check if this column is visible in category config
                const isColumnVisible = visibleColumns.some(col => col.header === prop.columnHeader);

                displayInfo.push({
                    label: `${prop.columnHeader}: ${prop.value}`,
                    isPrimary: visibleColumns.length > 0 && prop.columnHeader === visibleColumns[0]?.header,
                    isVendorProp: true,
                    columnHeader: prop.columnHeader,
                    isVisible: isColumnVisible
                });
            });
        }

        // Add material name if it exists and isn't already shown
        if (material.name && !displayInfo.some(d => d.label.includes(material.name))) {
            displayInfo.push({ label: `Material: ${material.name}`, isPrimary: false, isVisible: true });
        }

        // Add code
        if (material.code) {
            displayInfo.push({ label: `Code: ${material.code}`, isPrimary: false, isVisible: true });
        }

        // Add price
        if (material.selling_price_per_unit) {
            displayInfo.push({ label: `Price: ₹${material.selling_price_per_unit}/${material.unit}`, isPrimary: false, isVisible: true });
        }

        // Add any unmapped vendor properties as additional info
        if (material.vendorProperties) {
            const mappedNames = mappedProps.map(p => p.name);
            Object.entries(material.vendorProperties).forEach(([key, val]) => {
                if (val && typeof val === 'string' && !mappedNames.includes(key)) {
                    displayInfo.push({ label: `${key}: ${val}`, isPrimary: false, isVendorProp: true, isVisible: false });
                }
            });
        }

        return displayInfo;
    };

    if (disabled) {
        return (
            <div className="form-control bg-light" style={{ minHeight: '38px' }}>
                {value ? getDisplayText() : <span className="text-muted">{placeholder}</span>}
            </div>
        );
    }

    return (
        <div className="material-selector position-relative" ref={wrapperRef}>
            {/* Selected value display */}
            <div
                className="form-control d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer', minHeight: '38px' }}
                onClick={() => setIsOpen(!isOpen)}
            >
                {value ? (
                    // <span className="text-truncate">{getPrimaryDisplayValue(value)}</span>
                    <span className="text-truncate">{getDisplayText(value)}</span>
                ) : (
                    <span className="text-muted">{placeholder}</span>
                )}
                <div className="d-flex gap-2">
                    {value && (
                        <i
                            className="bi bi-x-circle-fill text-muted"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClear();
                            }}
                        ></i>
                    )}
                    <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} text-muted`}></i>
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="dropdown-menu show p-2" style={{
                    width: '100%',
                    maxHeight: '400px',
                    overflow: 'auto',
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    {/* Search input */}
                    <div className="position-relative mb-2">
                        <i className="bi bi-search position-absolute" style={{ left: '10px', top: '8px', color: '#999' }}></i>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            style={{ paddingLeft: '30px' }}
                            placeholder="Search by book name, design, shade, or material name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                        <small className="text-muted d-block mt-1">
                            <i className="bi bi-info-circle me-1"></i>
                            Searching across all vendor properties and material details
                        </small>
                    </div>

                    {/* Loading state */}
                    {loading && (
                        <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <div className="mt-1 small text-muted">Searching materials...</div>
                        </div>
                    )}

                    {/* Results */}
                    {!loading && materials.length > 0 && (
                        <div className="list-group">
                            {materials.map(material => {
                                const displayInfo = getMaterialDisplayInfo(material);
                                const visibleInfo = displayInfo.filter(info => info.isVisible !== false);

                                return (
                                    <button
                                        key={material._id}
                                        className="list-group-item list-group-item-action"
                                        onClick={() => handleSelect(material)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                {visibleInfo.map((info, idx) => (
                                                    <div key={idx}>
                                                        {info.isPrimary ? (
                                                            <strong className="text-primary">{info.label}</strong>
                                                        ) : info.isVendorProp ? (
                                                            <small className="text-muted d-block">
                                                                <i className="bi bi-tag me-1"></i>
                                                                {info.label}
                                                            </small>
                                                        ) : (
                                                            <small className="text-muted d-block">
                                                                {info.label}
                                                            </small>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="ms-2">
                                                <span className="badge bg-primary">
                                                    ₹{material.selling_price_per_unit}/{material.unit}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Show material properties if available */}
                                        {material.properties && Object.keys(material.properties).length > 0 && (
                                            <div className="mt-1">
                                                {material.properties.gsm && (
                                                    <span className="badge bg-secondary me-1">GSM: {material.properties.gsm}</span>
                                                )}
                                                {material.properties.fabricWidth && (
                                                    <span className="badge bg-secondary">Width: {material.properties.fabricWidth}"</span>
                                                )}
                                            </div>
                                        )}

                                        {/* Show vendor name */}
                                        {material.vendor?.name && (
                                            <div className="mt-1">
                                                <small className="text-muted">
                                                    <i className="bi bi-building me-1"></i>
                                                    Vendor: {material.vendor.name}
                                                </small>
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* No results */}
                    {!loading && searchTerm.length >= 2 && materials.length === 0 && (
                        <div className="text-center py-3 text-muted">
                            <i className="bi bi-search fs-4 d-block mb-2"></i>
                            No materials found matching "{searchTerm}"
                            <div className="small mt-1">Try searching by different keywords</div>
                        </div>
                    )}

                    {/* Search hint */}
                    {!loading && searchTerm.length < 2 && (
                        <div className="text-center py-3 text-muted">
                            <i className="bi bi-search fs-4 d-block mb-2"></i>
                            Type at least 2 characters to search
                            <div className="small mt-1">You can search by any vendor property or material detail</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MaterialSelector;